import cn from 'clsx'
import { FC, memo } from 'react'

import { Typography } from '@/components/ui'

import { ITranscription } from '@/types/task.interface'
import { displayTime } from '@/utils/displayTime'

import { type TColoredHighlightRange } from '../helpers/getHighlightedText'
import { getHighlightedChunkText } from '../helpers/transcriptionText'

interface ITranscriptionChunkProps {
	item: ITranscription
	index: number
	isCollapsed?: boolean
	highlightable?: boolean
	coloredHighlights: TColoredHighlightRange[]
	refCollector?: (el: HTMLDivElement | null, index: number) => void
	onRendered?: () => void
	isLast: boolean
	onSeek?: (sec: number) => void
	translation?: boolean
}

const TranscriptionChunkComponent: FC<ITranscriptionChunkProps> = ({
	item,
	index,
	isCollapsed,
	highlightable,
	coloredHighlights,
	refCollector,
	onRendered,
	isLast,
	onSeek,
	translation
}) => {
	const highlightedParts = getHighlightedChunkText(item, coloredHighlights)

	return (
		<div
			className={cn('mb-5', isCollapsed && 'w-3/4')}
			data-index={index}
			data-transcription-chunk-index={highlightable ? index : undefined}
			ref={el => {
				refCollector?.(el, index)
				if (isLast && el) {
					onRendered?.()
				}
			}}
		>
			<Typography
				variant='time'
				className={cn('text-gray-600/50', translation && 'cursor-pointer')}
				onClick={() => onSeek?.(item.start)}
			>
				{displayTime(item.start)} - {displayTime(item.end)}
			</Typography>

			<Typography
				variant='text-large'
				className='text-gray-600 selection:bg-blue-400'
				data-transcription-text
				data-start={item.start}
				data-end={item.end}
			>
				{highlightedParts.map((part, partIndex) => (
					<span
						key={partIndex}
						style={
							part.color
								? {
										backgroundColor:
											part.type === 'keyword' ? part.color : `${part.color}4D`
								  }
								: undefined
						}
						className={cn(
							highlightable && 'highlightable py-[4px] px-[2px] rounded-[4px]',
							part.type === 'keyword' && 'text-white'
						)}
					>
						{part.text}
					</span>
				))}
			</Typography>
		</div>
	)
}

const TranscriptionChunk = memo(TranscriptionChunkComponent)

export default TranscriptionChunk
